import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { AuthService } from './auth/services/auth.service';
import { breakpoints } from './consts/app-breakpoints';
import { ProfileComponent } from './dialogs/profile/profile.component';
import {
  InsightsReport,
  InsightsReportParams,
  InsightsVendor,
} from './reports/reports/general-report/interfaces/insights-reports';
import { GeneralReportService } from './reports/reports/general-report/services/general-report.service';
import { BeaconService } from './services/beacon.service';
import { StorageKeys, StorageService } from './services/local-storage.service';
import { RefreshService } from './services/refresh.service';

@Component({
  selector: 'opt-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild(MatSidenavContainer) sidenav: MatSidenavContainer;

  public title: string;
  public isProfileOpened = false;
  public isMobile = false;
  public isTabletPortrait = false;

  public isLogoIconLoaded = false;
  public isLogoTextLoaded = false;
  public reports: InsightsReport[] = [];
  public vendors: InsightsVendor[] = [];

  private _isLoading = true;
  private _currentRoute: string | undefined;
  private _permissions: string[] | undefined;
  private _isSidenavCollapsed = false;
  private _isSidenavOpen = false;
  private _subscriptions = new Subscription();

  constructor(
    private matIconRegistry: MatIconRegistry,
    public authService: AuthService,
    public auth0: Auth0Service,
    public refreshService: RefreshService,
    public generalReportService: GeneralReportService,
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private storageService: StorageService,
    private route: ActivatedRoute,
    private router: Router,
    private beaconService: BeaconService
  ) {}

  get isRouteLoading(): boolean {
    return !Boolean(this._currentRoute) || this._isLoading;
  }

  get isOptimalInsightsNav(): boolean {
    return this.isOptimalInsightsRoute(this._currentRoute);
  }

  get hasTopNav(): boolean {
    return this.isMobile || this.isOptimalInsightsNav;
  }

  get isSingleReport(): boolean {
    return this.reports.length < 2;
  }

  get isSingleVendor(): boolean {
    return this.vendors.length < 2;
  }

  get opened(): boolean {
    if (this.hasTopNav) {
      return this._isSidenavOpen;
    }
    return true;
  }

  get mode(): 'side' | 'over' {
    return this.hasTopNav ? 'over' : 'side';
  }

  get hasBackdrop(): boolean {
    return this.isMobile;
  }

  get disableClose(): boolean {
    return !this.isMobile;
  }

  get isLogoLoaded(): boolean {
    return this.isLogoIconLoaded && this.isLogoTextLoaded;
  }

  get featureFlags() {
    return environment.featureFlags;
  }

  get isSidenavCollapsed(): boolean {
    if (this.isMobile) {
      return false;
    }
    return this._isSidenavCollapsed;
  }

  set isSidenavCollapsed(value: boolean) {
    this._isSidenavCollapsed = value;
    this.sidenav?.updateContentMargins();
  }

  ngOnInit(): void {
    this.title = environment.projectName;
    this.matIconRegistry.setDefaultFontSetClass('material-icons-outlined');
    this.beaconService.init();
    this._subscriptions.add(
      this.breakpointObserver
        .observe(['(min-width: 1350px)'])
        .subscribe((state) => (this.isSidenavCollapsed = !state.matches))
    );

    this._subscriptions.add(
      this.breakpointObserver
        .observe([breakpoints.upto.medium])
        .subscribe((result) => {
          this.isMobile = result.matches;
        })
    );

    this._subscriptions.add(
      this.breakpointObserver
        .observe([Breakpoints.TabletPortrait])
        .subscribe((result) => {
          this.isTabletPortrait = result.matches;
        })
    );

    this._subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          const previousUrl = this._currentRoute;
          this._currentRoute = event.urlAfterRedirects;

          this.handleCloseNavOnRouteChange(previousUrl);
          this.handleUserAndRouteLoaded();

          if (this.isOptimalInsightsNav) {
            this.loadReportsAndVendors();

            // Optimal Insights users may still have been loading
            this._isLoading = false;
          }
        })
    );
    this.loadPermissions();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public onProfileClick(): void {
    const dialogRef = this.dialog.open(ProfileComponent, { maxWidth: '100vw' });
    this.isProfileOpened = true;
    dialogRef.afterClosed().subscribe(() => {
      this.isProfileOpened = false;
    });
  }

  public onSidebarToggle(): void {
    if (this.hasTopNav) {
      this._isSidenavOpen = !this._isSidenavOpen;
    } else {
      this.isSidenavCollapsed = !this.isSidenavCollapsed;
    }
  }

  public onNavigate(): void {
    if (this.hasTopNav) {
      this._isSidenavOpen = false;
    }
  }

  public trackReportsBy(_index: number, report: InsightsReport): string {
    return report.reportId;
  }

  public trackVendorsBy(_index: number, vendor: InsightsVendor): number {
    return vendor.id;
  }

  public getReportParams(report: InsightsReport) {
    return {
      reportId: report.reportId,
      workspaceId: report.workspaceId,
    };
  }

  public getVendorParams(vendor: InsightsVendor) {
    return {
      vendorId: vendor.id,
    };
  }

  isActive(path: string): boolean {
    return this.router.url.includes(path);
  }

  private async loadPermissions() {
    const token = await this.authService.getAuth0Token();
    const decoded = this.authService.decodeJwt(token);
    const permissions = decoded.permissions;
    this._permissions = permissions;
    this.handleUserAndRouteLoaded();
  }

  // When both user and route are loaded,
  // handle Optimal Insights redirect logic
  private handleUserAndRouteLoaded(): void {
    if (!this._permissions) return;
    if (!this._currentRoute) return;

    if (
      this._currentRoute === '/' &&
      this.authService.optimalInsightsOnly(this._permissions)
    ) {
      this.router.navigate(['/reports/insights']);
      return;
    }
    this._isLoading = false;
  }

  private async loadReportsAndVendors() {
    if (!this._permissions?.includes('optimal-insights-access')) return;

    const [params, vendors] = await Promise.all([
      this.route.queryParamMap.pipe(take(1)).toPromise(),
      this.generalReportService.getInsightsVendors().toPromise(),
    ]);

    const paramVendorId = params.get('vendorId')
      ? Number(params.get('vendorId'))
      : undefined;
    const storedValues =
      this.storageService.get<InsightsReportParams | null>(
        StorageKeys.insightsSelection
      ) ?? ({} as InsightsReportParams);
    const storedVendorId = storedValues.vendorId;
    const firstVendorId = vendors[0]?.id;
    const vendorId = paramVendorId ?? storedVendorId ?? firstVendorId;

    const reports = await this.generalReportService
      .getInsightsVendorReports(vendorId)
      .toPromise();

    this.vendors = vendors;
    this.reports = reports;
  }

  private handleCloseNavOnRouteChange(previousUrl: string): void {
    if (
      Boolean(previousUrl) &&
      this.isOptimalInsightsRoute(previousUrl) &&
      !this.isOptimalInsightsRoute(this._currentRoute)
    ) {
      this._isSidenavOpen = false;
    }
  }

  private isOptimalInsightsRoute(route: string): boolean {
    return (
      route.includes('/reports/insights') ||
      route.includes('/inventory/optimal-insights')
    );
  }
}
